import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Form,
  Message,
  Icon,
  Header,
  Grid,
  List,
} from "semantic-ui-react";
import { authenticationService } from "@authentication/services/AuthenticationService";

// 🚩 ADD: Import AuditService and userApi for logging
import { AuditService } from "@api/audit/audit";
import { userApi } from "@api/users/user";

const INITIAL_STATE = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  isSubmitting: false,
  hasError: false,
  errorMessage: "",
  hasSuccess: false,
  successMessage: "",
  validationErrors: {},
};

class ChangePasswordForm extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
  }

  validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push(
        "Password must contain at least one special character (@$!%*?&)"
      );
    }
    return errors;
  };

  validateForm = () => {
    const { currentPassword, newPassword, confirmPassword } = this.state;
    const errors = {};
    if (!currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }
    if (!newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else {
      const passwordErrors = this.validatePassword(newPassword);
      if (passwordErrors.length > 0) {
        errors.newPassword = passwordErrors[0];
      }
    }
    if (!confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    if (currentPassword && newPassword && currentPassword === newPassword) {
      errors.newPassword =
        "New password must be different from current password";
    }
    return errors;
  };

  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      validationErrors: {
        ...this.state.validationErrors,
        [field]: null,
      },
      hasError: false,
      hasSuccess: false,
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = this.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      this.setState({
        validationErrors,
        hasError: true,
        errorMessage: "Please correct the errors below",
      });
      return;
    }

    this.setState({ isSubmitting: true, hasError: false, hasSuccess: false });

    try {
      const { currentPassword, newPassword } = this.state;

      const response = await authenticationService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      // 🚩 ADD: Audit logging after successful password change
      const userResponse = await userApi.get();
      const currentUser = userResponse?.data || {
        username: "Unknown",
        full_name: "Unknown User",
      };

      const action = "Change Password";
      const details = `User ${currentUser.full_name}  changed their password successfully.`;

      await AuditService.api.createLog({
        action,
        details,
        created_by: `${currentUser.full_name} `,
      });

      this.setState({
        ...INITIAL_STATE,
        hasSuccess: true,
        successMessage:
          "Password changed successfully! For security, you will be logged out shortly.",
      });

      if (this.props.onPasswordChanged) {
        this.props.onPasswordChanged(response.data);
      }

      setTimeout(() => {
        if (
          window.confirm(
            "Password changed successfully! You will be logged out for security. Click OK to continue."
          )
        ) {
          authenticationService.logout();
        }
      }, 500);
    } catch (error) {
      this.setState({
        isSubmitting: false,
        hasError: true,
        errorMessage:
          error.message || "Failed to change password. Please try again.",
      });
    }
  };

  handleReset = () => {
    this.setState(INITIAL_STATE);
  };

  render() {
    const {
      currentPassword,
      newPassword,
      confirmPassword,
      isSubmitting,
      hasError,
      errorMessage,
      hasSuccess,
      successMessage,
      validationErrors,
    } = this.state;

    return (
      <div className="change-password-container">
        <div className="change-password-card">
          <div className="change-password-header">
            <div className="change-password-header-decoration"> </div>
            <Header as="h1" className="change-password-title">
              <Icon name="key" className="change-password-icon-key" />
              Change Password
            </Header>
            <p className="change-password-subtitle">
              Update your account password for enhanced security
            </p>
          </div>

          <div className="change-password-content">
            <Grid stackable>
              <Grid.Column
                mobile={16}
                tablet={10}
                computer={10}
                className="change-password-form-section"
              >
                {hasError && (
                  <Message negative style={{ marginBottom: "1.5rem" }}>
                    <Message.Header>
                      <Icon name="times circle" />
                      Password Change Failed
                    </Message.Header>
                    <p>{errorMessage}</p>
                    {Object.keys(validationErrors).length > 0 && (
                      <List bulleted>
                        {Object.values(validationErrors).map(
                          (error, index) =>
                            error && <List.Item key={index}>{error}</List.Item>
                        )}
                      </List>
                    )}
                  </Message>
                )}
                {hasSuccess && (
                  <Message positive style={{ marginBottom: "1.5rem" }}>
                    <Message.Header>
                      <Icon name="check circle" />
                      Success
                    </Message.Header>
                    <p>{successMessage}</p>
                  </Message>
                )}

                <Form onSubmit={this.handleSubmit}>
                  <div className="change-password-form-group">
                    <label className="change-password-label">
                      Current Password
                      <span className="change-password-required-asterisk">
                        *
                      </span>
                    </label>
                    <Form.Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) =>
                        this.handleInputChange(
                          "currentPassword",
                          e.target.value
                        )
                      }
                      placeholder="Enter your current password"
                      error={!!validationErrors.currentPassword}
                      icon="lock"
                      iconPosition="left"
                      autoFocus
                      required
                    />
                    {validationErrors.currentPassword && (
                      <div className="change-password-error-text">
                        <Icon name="times circle" />
                        {validationErrors.currentPassword}
                      </div>
                    )}
                  </div>

                  <div className="change-password-form-group">
                    <label className="change-password-label">
                      New Password
                      <span className="change-password-required-asterisk">
                        *
                      </span>
                    </label>
                    <Form.Input
                      type="password"
                      value={newPassword}
                      onChange={(e) =>
                        this.handleInputChange("newPassword", e.target.value)
                      }
                      placeholder="Enter your new password"
                      error={!!validationErrors.newPassword}
                      icon="key"
                      iconPosition="left"
                      required
                    />
                    {validationErrors.newPassword && (
                      <div className="change-password-error-text">
                        <Icon name="times circle" />
                        {validationErrors.newPassword}
                      </div>
                    )}
                  </div>

                  <div className="change-password-form-group">
                    <label className="change-password-label">
                      Confirm New Password
                      <span className="change-password-required-asterisk">
                        *
                      </span>
                    </label>
                    <Form.Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) =>
                        this.handleInputChange(
                          "confirmPassword",
                          e.target.value
                        )
                      }
                      placeholder="Confirm your new password"
                      error={!!validationErrors.confirmPassword}
                      icon="shield alternate"
                      iconPosition="left"
                      required
                    />
                    {validationErrors.confirmPassword && (
                      <div className="change-password-error-text">
                        <Icon name="times circle" />
                        {validationErrors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <div className="change-password-form-actions">
                    <Button
                      type="button"
                      onClick={this.handleReset}
                      disabled={isSubmitting}
                      basic
                    >
                      <Icon name="undo" />
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      primary
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="change-password-update-button"
                    >
                      <Icon name="key" />
                      Change Password
                    </Button>
                  </div>
                </Form>
              </Grid.Column>

              <Grid.Column
                mobile={16}
                tablet={6}
                computer={6}
                className="change-password-tips-section"
              >
                <div className="change-password-security-tips">
                  <Header as="h5" style={{ marginBottom: "1rem" }}>
                    <Icon name="shield alternate" />
                    Password Security Tips
                  </Header>
                  <List bulleted className="change-password-tips-list">
                    <List.Item>
                      Use at least 8 characters with a mix of letters, numbers,
                      and symbols
                    </List.Item>
                    <List.Item>
                      Avoid using personal information or common words
                    </List.Item>
                    <List.Item>
                      Don't reuse passwords from other accounts
                    </List.Item>
                    <List.Item>
                      Consider using a password manager for better security
                    </List.Item>
                  </List>
                </div>
              </Grid.Column>
            </Grid>
          </div>
        </div>
      </div>
    );
  }
}

ChangePasswordForm.propTypes = {
  onPasswordChanged: PropTypes.func,
};

ChangePasswordForm.defaultProps = {
  onPasswordChanged: null,
};

export default ChangePasswordForm;
